import { useEffect, useMemo, useState } from "react";
import { fmtDate } from "@/lib/dates";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { TrendingUp, TrendingDown, Scale, Plus, Trash2, Download, FileText, FileSpreadsheet } from "lucide-react";

const money = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const EXPENSE_CATS = ["Rent", "Benevolent Fund", "Salaries", "Missions Fund", "Subscriptions", "Printing Stationeries & Supplies", "Other"];
const INCOME_SOURCES = ["Tithes", "Offerings", "Pastor's Fund", "Missions Fund", "Benevolent Fund", "Other"];

function periodRange(key) {
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const y = now.getFullYear();
  const m = now.getMonth();
  if (key === "this_month") return { start: iso(new Date(y, m, 1)), end: iso(now) };
  if (key === "last_month") return { start: iso(new Date(y, m - 1, 1)), end: iso(new Date(y, m, 0)) };
  if (key === "this_year") return { start: `${y}-01-01`, end: iso(now) };
  return { start: "", end: "" };
}

const PERIODS = [
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "this_year", label: "This Year" },
  { key: "all", label: "All Time" },
  { key: "custom", label: "Custom Range" },
];

export default function Finances() {
  const [period, setPeriod] = useState("this_month");
  const [summary, setSummary] = useState(null);
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [incOpen, setIncOpen] = useState(false);
  const [expOpen, setExpOpen] = useState(false);
  const [incForm, setIncForm] = useState({ source: "Tithes & Offerings", amount: "", date: new Date().toISOString().slice(0, 10), notes: "" });
  const [expForm, setExpForm] = useState({ category: "Rent", amount: "", date: new Date().toISOString().slice(0, 10), description: "" });
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState(new Date().toISOString().slice(0, 10));
  const [compGran, setCompGran] = useState("month");
  const [compStart, setCompStart] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 5); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); });
  const [compEnd, setCompEnd] = useState(new Date().toISOString().slice(0, 10));
  const [compData, setCompData] = useState(null);

  const range = useMemo(
    () => (period === "custom" ? { start: customStart, end: customEnd } : periodRange(period)),
    [period, customStart, customEnd]
  );

  const loadSummary = () => {
    const qs = new URLSearchParams();
    if (range.start) qs.set("start", range.start);
    if (range.end) qs.set("end", range.end);
    api.get(`/finance/summary?${qs.toString()}`).then((r) => setSummary(r.data)).catch(() => {});
  };
  const loadLists = () => {
    api.get("/finance/income").then((r) => setIncome(r.data)).catch(() => {});
    api.get("/finance/expenses").then((r) => setExpenses(r.data)).catch(() => {});
  };
  useEffect(() => { loadLists(); }, []);
  useEffect(loadSummary, [period, customStart, customEnd]); // eslint-disable-line

  const loadComparative = () => {
    const qs = new URLSearchParams({ granularity: compGran });
    if (compStart) qs.set("start", compStart);
    if (compEnd) qs.set("end", compEnd);
    api.get(`/finance/comparative?${qs.toString()}`).then((r) => setCompData(r.data)).catch(() => {});
  };
  useEffect(loadComparative, [compGran, compStart, compEnd]); // eslint-disable-line

  const exportComparative = async (fmt) => {
    try {
      const qs = new URLSearchParams({ format: fmt, granularity: compGran });
      if (compStart) qs.set("start", compStart);
      if (compEnd) qs.set("end", compEnd);
      const res = await api.get(`/finance/comparative/export?${qs.toString()}`, { responseType: "blob" });
      const type = fmt === "pdf" ? "application/pdf" : "text/csv";
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement("a");
      a.href = url; a.download = `comparative-${compGran}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch { toast.error("Export failed"); }
  };

  const inRange = (d) => (!range.start || d >= range.start) && (!range.end || d <= range.end);
  const visibleIncome = income.filter((i) => inRange(i.date));
  const visibleExpenses = expenses.filter((e) => inRange(e.date));

  const saveIncome = async () => {
    if (!incForm.source || !incForm.amount) { toast.error("Source and amount are required"); return; }
    try {
      await api.post("/finance/income", { ...incForm, amount: parseFloat(incForm.amount) });
      toast.success("Income recorded");
      setIncOpen(false);
      setIncForm({ ...incForm, amount: "", notes: "" });
      loadLists(); loadSummary(); loadComparative();
    } catch { toast.error("Could not save income"); }
  };
  const saveExpense = async () => {
    if (!expForm.category || !expForm.amount) { toast.error("Category and amount are required"); return; }
    try {
      await api.post("/finance/expenses", { ...expForm, amount: parseFloat(expForm.amount) });
      toast.success("Expense recorded");
      setExpOpen(false);
      setExpForm({ ...expForm, amount: "", description: "" });
      loadLists(); loadSummary(); loadComparative();
    } catch { toast.error("Could not save expense"); }
  };
  const delIncome = async (id) => { try { await api.delete(`/finance/income/${id}`); loadLists(); loadSummary(); loadComparative(); } catch { toast.error("Could not remove"); } };
  const delExpense = async (id) => { try { await api.delete(`/finance/expenses/${id}`); loadLists(); loadSummary(); loadComparative(); } catch { toast.error("Could not remove"); } };

  const exportFinance = async (fmt) => {
    try {
      const qs = new URLSearchParams({ format: fmt });
      if (range.start) qs.set("start", range.start);
      if (range.end) qs.set("end", range.end);
      const res = await api.get(`/finance/export?${qs.toString()}`, { responseType: "blob" });
      const type = fmt === "pdf" ? "application/pdf" : "text/csv";
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement("a");
      a.href = url; a.download = `financial-report-${period}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch { toast.error("Export failed"); }
  };

  const balancePositive = (summary?.balance ?? 0) >= 0;

  return (
    <div className="space-y-6" data-testid="finances-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-[#526050]">Board income & expense ledger</p>
        <div className="flex gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-48 h-11 bg-white" data-testid="finance-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="finance-export-button" className="h-11 border-[#2D5A27] text-[#2D5A27] hover:bg-[#EAF2E8]">
                <Download className="h-4 w-4 mr-2" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              <DropdownMenuItem data-testid="finance-export-csv" onClick={() => exportFinance("csv")}>
                <FileSpreadsheet className="h-4 w-4 mr-2 text-[#2D5A27]" /> Download CSV
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="finance-export-pdf" onClick={() => exportFinance("pdf")}>
                <FileText className="h-4 w-4 mr-2 text-[#9A3412]" /> Download PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {period === "custom" && (
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-[#E5E0D8] bg-white p-4" data-testid="custom-range-panel">
          <div className="space-y-1.5">
            <Label className="text-xs">Start date</Label>
            <Input type="date" data-testid="custom-start-input" value={customStart} max={customEnd || undefined}
              onChange={(e) => setCustomStart(e.target.value)} className="h-10" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">End date</Label>
            <Input type="date" data-testid="custom-end-input" value={customEnd} min={customStart || undefined}
              onChange={(e) => setCustomEnd(e.target.value)} className="h-10" />
          </div>
          <p className="text-sm text-[#7A8A78] pb-2.5">
            {customStart || customEnd ? `Showing ${customStart ? fmtDate(customStart) : "beginning"} to ${customEnd ? fmtDate(customEnd) : "today"}` : "Pick a start and end date"}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <Card className="p-6 bg-white border border-[#E5E0D8] shadow-sm" data-testid="stat-total-income">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-[#7A8A78]">Total Income</p>
              <p className="mt-3 font-serif text-3xl font-bold text-[#2D5A27]">{money(summary?.total_income || 0)}</p>
              <p className="mt-1 text-sm text-[#526050]">{summary?.income_count || 0} entries</p>
            </div>
            <div className="h-11 w-11 rounded-xl bg-[#2D5A27] flex items-center justify-center"><TrendingUp className="h-5 w-5 text-white" /></div>
          </div>
        </Card>
        <Card className="p-6 bg-white border border-[#E5E0D8] shadow-sm" data-testid="stat-total-expenses">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-[#7A8A78]">Total Expenses</p>
              <p className="mt-3 font-serif text-3xl font-bold text-[#9A3412]">{money(summary?.total_expenses || 0)}</p>
              <p className="mt-1 text-sm text-[#526050]">{summary?.expense_count || 0} entries</p>
            </div>
            <div className="h-11 w-11 rounded-xl bg-[#9A3412] flex items-center justify-center"><TrendingDown className="h-5 w-5 text-white" /></div>
          </div>
        </Card>
        <Card className={`p-6 border shadow-sm ${balancePositive ? "bg-[#2D5A27] border-[#2D5A27]" : "bg-[#9A3412] border-[#9A3412]"}`} data-testid="stat-balance">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-white/70">Balance</p>
              <p className="mt-3 font-serif text-3xl font-bold text-white">{money(summary?.balance || 0)}</p>
              <p className="mt-1 text-sm text-white/80">{balancePositive ? "Surplus" : "Deficit"} this period</p>
            </div>
            <div className="h-11 w-11 rounded-xl bg-white/20 flex items-center justify-center"><Scale className="h-5 w-5 text-white" /></div>
          </div>
        </Card>
      </div>

      <Card className="p-6 bg-white border border-[#E5E0D8] shadow-sm" data-testid="comparative-card">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-6">
          <div>
            <h3 className="font-serif text-xl font-semibold text-[#1C241B]">Comparative Analysis — Income vs Expenses</h3>
            <p className="text-sm text-[#7A8A78] mt-0.5">{compGran === "year" ? "Yearly" : "Monthly"} comparison{compData ? ` · ${fmtDate(compData.start)} to ${fmtDate(compData.end)}` : ""}</p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">View</Label>
              <Select value={compGran} onValueChange={setCompGran}>
                <SelectTrigger className="w-32 h-10 bg-white" data-testid="comparative-granularity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Monthly</SelectItem>
                  <SelectItem value="year">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">From</Label>
              <Input type="date" data-testid="comparative-start-input" value={compStart} max={compEnd || undefined} onChange={(e) => setCompStart(e.target.value)} className="h-10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">To</Label>
              <Input type="date" data-testid="comparative-end-input" value={compEnd} min={compStart || undefined} onChange={(e) => setCompEnd(e.target.value)} className="h-10" />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="comparative-export-button" className="h-10 border-[#2D5A27] text-[#2D5A27] hover:bg-[#EAF2E8]">
                  <Download className="h-4 w-4 mr-2" /> Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white">
                <DropdownMenuItem data-testid="comparative-export-csv" onClick={() => exportComparative("csv")}>
                  <FileSpreadsheet className="h-4 w-4 mr-2 text-[#2D5A27]" /> Download CSV
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="comparative-export-pdf" onClick={() => exportComparative("pdf")}>
                  <FileText className="h-4 w-4 mr-2 text-[#9A3412]" /> Download PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={compData?.buckets || []} margin={{ left: -10, right: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EAE6DF" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#7A8A78" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: "#7A8A78" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
            <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E5E0D8" }} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="income" name="Income" fill="#2D5A27" radius={[6, 6, 0, 0]} />
            <Bar dataKey="expenses" name="Expenses" fill="#9A3412" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        {(!compData || compData.buckets.length === 0) && (
          <p className="text-center text-sm text-[#7A8A78] py-4">No data in this range. Pick a wider date range.</p>
        )}
        {compData && compData.buckets.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm border-t border-[#E5E0D8] pt-4" data-testid="comparative-totals">
            <span className="text-[#526050]">Total Income: <b className="text-[#2D5A27]">{money(compData.total_income)}</b></span>
            <span className="text-[#526050]">Total Expenses: <b className="text-[#9A3412]">{money(compData.total_expenses)}</b></span>
            <span className="text-[#526050]">Net Balance: <b className={compData.balance >= 0 ? "text-[#2D5A27]" : "text-[#9A3412]"}>{money(compData.balance)}</b></span>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income */}
        <Card className="bg-white border border-[#E5E0D8] shadow-sm overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-[#E5E0D8]">
            <h3 className="font-serif text-xl font-semibold text-[#1C241B]">Income</h3>
            <Button size="sm" data-testid="add-income-button" onClick={() => setIncOpen(true)} className="bg-[#2D5A27] hover:bg-[#23471F] text-white">
              <Plus className="h-4 w-4 mr-1" /> Add Income
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-[#E5E0D8]">
                <TableHead>Date</TableHead><TableHead>Source</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleIncome.map((i) => (
                <TableRow key={i.id} className="border-[#E5E0D8]" data-testid={`income-row-${i.id}`}>
                  <TableCell className="text-sm text-[#526050]">{fmtDate(i.date)}</TableCell>
                  <TableCell className="font-medium text-[#1C241B]">{i.source}</TableCell>
                  <TableCell className="text-right font-semibold text-[#2D5A27]">{money(i.amount)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => delIncome(i.id)} data-testid={`delete-income-${i.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {visibleIncome.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-10 text-[#7A8A78]">No income in this period.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>

        {/* Expenses */}
        <Card className="bg-white border border-[#E5E0D8] shadow-sm overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-[#E5E0D8]">
            <h3 className="font-serif text-xl font-semibold text-[#1C241B]">Expenses</h3>
            <Button size="sm" data-testid="add-expense-button" onClick={() => setExpOpen(true)} className="bg-[#9A3412] hover:bg-[#7c2a0e] text-white">
              <Plus className="h-4 w-4 mr-1" /> Add Expense
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-[#E5E0D8]">
                <TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleExpenses.map((e) => (
                <TableRow key={e.id} className="border-[#E5E0D8]" data-testid={`expense-row-${e.id}`}>
                  <TableCell className="text-sm text-[#526050]">{fmtDate(e.date)}</TableCell>
                  <TableCell className="font-medium text-[#1C241B]">{e.category}{e.description ? <span className="block text-xs text-[#7A8A78]">{e.description}</span> : null}</TableCell>
                  <TableCell className="text-right font-semibold text-[#9A3412]">{money(e.amount)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => delExpense(e.id)} data-testid={`delete-expense-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {visibleExpenses.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-10 text-[#7A8A78]">No expenses in this period.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Income dialog */}
      <Dialog open={incOpen} onOpenChange={setIncOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="income-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Record Income</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Source</Label>
              <Select value={incForm.source} onValueChange={(v) => setIncForm({ ...incForm, source: v })}>
                <SelectTrigger data-testid="income-source-input"><SelectValue /></SelectTrigger>
                <SelectContent>{INCOME_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Amount</Label>
                <Input type="number" data-testid="income-amount-input" value={incForm.amount} onChange={(e) => setIncForm({ ...incForm, amount: e.target.value })} placeholder="0.00" /></div>
              <div className="space-y-1.5"><Label>Date</Label>
                <Input type="date" value={incForm.date} onChange={(e) => setIncForm({ ...incForm, date: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label>
              <Input value={incForm.notes} onChange={(e) => setIncForm({ ...incForm, notes: e.target.value })} placeholder="Optional" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIncOpen(false)}>Cancel</Button>
            <Button onClick={saveIncome} data-testid="save-income-button" className="bg-[#2D5A27] hover:bg-[#23471F] text-white">Record Income</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expense dialog */}
      <Dialog open={expOpen} onOpenChange={setExpOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="expense-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Record Expense</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={expForm.category} onValueChange={(v) => setExpForm({ ...expForm, category: v })}>
                <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{EXPENSE_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Amount</Label>
                <Input type="number" data-testid="expense-amount-input" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} placeholder="0.00" /></div>
              <div className="space-y-1.5"><Label>Date</Label>
                <Input type="date" value={expForm.date} onChange={(e) => setExpForm({ ...expForm, date: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Description</Label>
              <Input value={expForm.description} onChange={(e) => setExpForm({ ...expForm, description: e.target.value })} placeholder="Optional" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpOpen(false)}>Cancel</Button>
            <Button onClick={saveExpense} data-testid="save-expense-button" className="bg-[#9A3412] hover:bg-[#7c2a0e] text-white">Record Expense</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
